// エントリ（外枠）：SW登録 → 認証状態でアプリシェルを表示 → ルーターでモジュール切替。
import './styles.css'
import { supabaseReady } from './supabase.js'
import { watchAuth, login, logout } from './auth.js'
import { createRouter } from './router.js'
import { NAV_IDS, NAV_LABEL, EXTRA_LABEL } from './nav.js'
import { renderShell, highlightActive } from './shell.js'
import { views } from './views/index.js'
import { renderLogin } from './views/login.js'
import { wirePrototypeButtons } from './prototypeUrl.js'
// static import に統一（2026-08-27）: drivers.js が static import 済みで laborRates.js（旧 payroll.js）は
// 元々起動時に評価されており、dynamic と混在するとビルド警告（チャンク分離が効かない）が出るだけだった。
import { refreshLabor } from './data/laborRates.js'
import { refreshQuoteFuel } from './data/quoteFuel.js'
import { refreshCompany } from './data/companyInfo.js'
// デモデータ表示 ON/OFF（settings/display）。同じ理由で static import に統一。
import { refreshDemoSetting } from './data/demoData.js'
// 自社情報（settings/company）。請求書の右上ブロック・振込先の正本。既定は実物の請求書の値

const root = document.getElementById('app')

// テーマ初期化: 保存値を尊重（既定はダーク＝属性なし）。反転ボタンは shell.js
let savedTheme = null
try { savedTheme = localStorage.getItem('veil-theme') } catch {}
if (savedTheme === 'light') document.documentElement.dataset.theme = 'light'

// 得意先向け 承認ページのトークン（#/approve?token=…）。該当時は token 文字列（空文字含む）、非該当は null。
function approveRouteToken() {
  const h = location.hash || ''
  const m = h.match(/^#\/?approve(?:\?(.*))?$/i)
  if (!m) return null
  return new URLSearchParams(m[1] || '').get('token') || ''
}
function requestRouteToken() {
  const h = location.hash || ''
  const m = h.match(/^#\/?request(?:\?(.*))?$/i)
  if (!m) return null
  return new URLSearchParams(m[1] || '').get('token') || ''
}

function startApp({ user, demo }) {
  wirePrototypeButtons() // 「詳細プロトタイプ ↗」= 認証つき取得。静的URLは廃止した
  const viewEl = renderShell(root, {
    user,
    demo,
    onSignOut: async () => { await logout(); location.hash = ''; location.reload() },
    onSignIn: async () => { try { await login() } catch (e) { console.error(e) } },
  })
  const router = createRouter(NAV_IDS, (id) => {
    highlightActive(id, NAV_LABEL[id] || EXTRA_LABEL[id])
    const render = views[id] || views.dashboard
    render(viewEl, { user, demo })
  })
  router.start()
}

function showLogin() {
  renderLogin(root, { login })
}

// PWA: Service Worker 登録（新版を検知したら自動リロードで最新に更新＝Ctrl+Shift+R不要に）
// 開発時は登録しない: 過去の版のSWが古いシェルを配り続け、変更が画面に出ない事故が起きる
// （2026-08-25 に2回実測）。残骸のSW・キャッシュも掃除して開発ブラウザを自己修復する。
if ('serviceWorker' in navigator && import.meta.env.DEV) {
  navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {})
  if (window.caches) caches.keys().then((ks) => ks.forEach((k) => caches.delete(k))).catch(() => {})
} else if ('serviceWorker' in navigator) {
  const hadController = !!navigator.serviceWorker.controller
  let reloaded = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloaded) return // 初回インストール時はリロードしない
    reloaded = true
    location.reload()
  })
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').then((reg) => {
      setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000) // 開きっぱなしタブの更新確認
    }).catch(() => {})
  })
}

const _approveToken = approveRouteToken()
const _requestToken = requestRouteToken()
if (_approveToken !== null || _requestToken !== null) {
  // 得意先向け外部2画面は白が既定（レジストリ#14「得意先には白で開く」）。明示的にダークを選んだ人だけ尊重
  if (savedTheme !== 'dark') document.documentElement.dataset.theme = 'light'
}
if (_approveToken !== null || _requestToken !== null) {
  // 外部画面の表示中はルーターを持たない（createRouter は startApp の中だけ）。そのため
  // ハッシュが変わっても何も描き変わらず、URL と表示が食い違う（戻る/進む、別トークンのリンク）。
  // 完全リロードして、この起動時分岐にやり直させる＝常に URL どおりの画面になる。
  // ループしない: リロードで hashchange は発火しない。
  window.addEventListener('hashchange', () => location.reload())
}
if (_approveToken !== null) {
  // 得意先向け 承認ページ（ログイン不要・アプリシェル非依存）。動的importで本体バンドルから分離。
  import('./views/approve.js')
    .then((m) => m.renderApprove(root, _approveToken))
    .catch((e) => { console.error(e); root.innerHTML = '<div style="padding:24px;font-family:system-ui;color:#333">読み込みに失敗しました。時間をおいて再度お試しください。</div>' })
} else if (_requestToken !== null) {
  import('./views/request.js')
    .then((m) => m.renderRequest(root, _requestToken))
    .catch((e) => { console.error(e); root.innerHTML = '<div style="padding:24px;font-family:system-ui;color:#333">読み込みに失敗しました。時間をおいて再度お試しください。</div>' })
} else if (!supabaseReady) {
  // Supabase未設定でも土台を確認できるデモ表示
  startApp({ user: null, demo: true })
} else {
  let started = false
  // アプリシェルはログイン必須（2026-08-24）。未ログインの公開ビューは廃止した。
  // 廃止の理由: 未ログインだと Supabase から何も読めず sample.*.json へフォールバックするが、
  //   その中身が実CSV由来（実取引先名・実売上・乗務員実名）だったため、URLを知るだけで閲覧できた。
  // 得意先向けの #/approve・#/request は上の分岐で処理済み＝公開のまま。ここは社内画面だけを塞ぐ。
  watchAuth(async (user) => {
    if (started) return
    // 未ログインはログイン画面。started は立てない＝セッション復元が遅れて届いた場合に起動できるようにする。
    if (!user) { showLogin(); return }
    started = true
    // 人件費率設定（乗務員別率＋試算用率）とデモデータ表示設定を先読み。
    // デモ設定はビューが描かれる前に確定させる＝「設定が届く前に一瞬サンプルが出る」競合を作らない。
    // どちらも失敗しても起動は続行（人件費=初期値35%、デモ表示=既定OFF＝サンプルを出さない安全側）。
    // 試算の燃費（車格別・settings/assumptions）も先読み。失敗時は既定（中型4・大型2）。
    try { await Promise.all([refreshLabor(true), refreshDemoSetting(true), refreshQuoteFuel(true), refreshCompany(true)]) } catch (e) { console.error(e) }
    startApp({ user, demo: false })
  })
}
